import { useEffect } from 'react';

import { Header, setHeaders } from 'state/graphql/headers';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { appVersion } from 'utils/app-version-info';
import { getCrashlytics, setAttribute } from 'utils/crashlytics';
import { addContext as addLoggerContext } from 'utils/datadog';
import { getDeviceIdAsync } from 'utils/device-id';
import { getApiKey } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import logger from 'utils/logger';
import { TraceName } from 'utils/performance';
import { startTrace } from 'utils/performance/trace';

import {
  getSessionId,
  initAmplitudeWithPluginsForPlatform,
  setDeviceId as setAmplitudeDeviceId,
  setOptOut,
} from './amplitude-package';

import { updateAmplitudeUserAttributes } from './index';

const amplitudeKey = getApiKey({ key: 'amplitude' });
const staticAmplitudeSessionId = { current: 'UN-INITIALIZED' };

export const initializeAmplitudePromise = initializeAmplitude();

/**
 * Initialize the Amplitude client.
 * If a user is already logged in, we will initialize with the user's cognito ID.
 */
async function initializeAmplitude() {
  const trace = startTrace(TraceName.APP_START_INIT_AMPLITUDE);

  try {
    const cognitoId = syncStorage.getString(SyncStorageKey.USER_ID);

    const options = {
      trackingSessionEvents: true,
      appVersion,
      autocapture: {
        sessions: true,
        pageViews: false,
        attribution: false,
      },
      trackingOptions: {
        appSetId: true,
        idfv: true,
      },
    };

    await initAmplitudeWithPluginsForPlatform(amplitudeKey ?? '', cognitoId || undefined, options);
    if (cognitoId) {
      updateAmplitudeUserAttributes();
    }

    const amplitudeSessionId = getSessionId()?.toString() ?? '';

    addLoggerContext('amplitudeSession', amplitudeSessionId);
    setAttribute(getCrashlytics(), 'amplitudeSession', amplitudeSessionId);
    setHeaders(Header.SESSION_ID, amplitudeSessionId);
    staticAmplitudeSessionId.current = amplitudeSessionId;

    setAmplitudeDeviceId(await getDeviceIdAsync());
  } catch (error) {
    // this is temporary until we remove this code from the backend as this timestamp amp session id
    // is being used for security checks
    setHeaders(Header.SESSION_ID, new Date().getTime().toString());
    logger.error({ error, message: 'Unknown amplitude initialization error' });
  } finally {
    trace?.stop();
  }
}

export const useAmplitudeOptOut = () => {
  const enableOptionalAmplitudeTracking = useFlag(
    LaunchDarklyFlag.ENABLE_OPTIONAL_AMPLITUDE_TRACKING
  );

  useEffect(() => {
    const cognitoId = syncStorage.getString(SyncStorageKey.USER_ID);

    // If we turn on optional Amplitude tracking
    // users will be able to opt out of Amplitude tracking.
    // If we decide that we do not want to disable events,
    // when we disable the flag (making Amplitude tracking not-optional),
    // we need to change setOptOut to false in order to send events to Amplitude.
    // This would guarantee any users who previously opted out, will be opted back in.
    // We should always faithfully record in local storage + Amplitude what the user wants us to do.
    // If we have good reason to not respect their preferences - that is where we should override their preferences.
    // REVISIT
    if (cognitoId && !enableOptionalAmplitudeTracking) {
      setOptOut(false);
    }
  }, [enableOptionalAmplitudeTracking]);
};
